import {
    Box,
    Button,
    Dialog,
    DialogContent,
    IconButton,
    Switch,
    TextField,
    Typography,
} from "@mui/material";
import {Close as CloseIcon, VpnKey as KeyIcon} from "@mui/icons-material";
import {useFormik} from "formik";
import {useState} from "react";
import {RTL} from "../../cache/CacheRtl";
import {createApiKey, updateApiKey} from "../../services/ApiKeyService";
import {useMessageContext} from "../../contexts/MessageContext";
import {getErrorMessage} from "../../enums/ErrorMessages";
import {borders, colors} from "../ui/colors";

const labelSx = {fontSize: "13px", fontWeight: 500, color: colors.textSecondary, mb: 0.5};

const ApiKeyDialog = ({open, apiKey, companyId, onClose, onSaved}) => {
    const isEdit = Boolean(apiKey);
    const {notificationHandler} = useMessageContext();
    const [submitting, setSubmitting] = useState(false);
    const [createdKey, setCreatedKey] = useState(null);

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: {
            name: apiKey?.name ?? "",
            enabled: apiKey?.enabled ?? true,
        },
        validate: (values) => {
            const errors = {};
            if (!values.name?.trim()) errors.name = "שדה חובה";
            return errors;
        },
        onSubmit: async (values) => {
            setSubmitting(true);
            try {
                if (isEdit) {
                    await updateApiKey(apiKey.id, values);
                    notificationHandler.success("המפתח עודכן");
                    onSaved && onSaved();
                    onClose();
                } else {
                    const res = await createApiKey(values, companyId);
                    setCreatedKey(res.data?.keyValue);
                    notificationHandler.success("המפתח נוצר");
                    onSaved && onSaved();
                }
            } catch (e) {
                notificationHandler.error(getErrorMessage(e.response?.data?.errorCode));
            } finally {
                setSubmitting(false);
            }
        },
    });

    const handleClose = () => {
        setCreatedKey(null);
        onClose();
    };

    const handleCopy = () => {
        if (!createdKey) return;
        navigator.clipboard.writeText(createdKey).catch(() => {});
        notificationHandler.success("הועתק");
    };

    return (
        <Dialog open={open} onClose={handleClose}
                PaperProps={{sx: {borderRadius: borders.radius.lg, minWidth: {xs: "95%", md: "520px"}}}}>
            <RTL>
                <Box dir="rtl">
                    <Box sx={{
                        display: "flex", justifyContent: "space-between", alignItems: "center",
                        padding: "16px 20px", backgroundColor: colors.bg, borderBottom: borders.default
                    }}>
                        <Box sx={{display: "flex", alignItems: "center", gap: 1}}>
                            <KeyIcon sx={{color: colors.textSecondary}}/>
                            <Typography sx={{fontWeight: 600, fontSize: "16px"}}>
                                {isEdit ? "עריכת מפתח API" : "מפתח API חדש"}
                            </Typography>
                        </Box>
                        <IconButton onClick={handleClose} size="small"><CloseIcon fontSize="small"/></IconButton>
                    </Box>

                    {createdKey ? (
                        <DialogContent sx={{padding: "24px 20px"}}>
                            <Typography sx={{mb: 1, fontSize: "13px"}}>המפתח נוצר. העתק אותו עכשיו.</Typography>
                            <Box sx={{
                                display: "flex", gap: 1, alignItems: "center",
                                p: 1.5, border: borders.default, borderRadius: borders.radius.md,
                                backgroundColor: colors.bg, fontFamily: "monospace", fontSize: "13px"
                            }}>
                                <Box sx={{flex: 1, wordBreak: "break-all"}}>{createdKey}</Box>
                                <Button size="small" onClick={handleCopy}>העתק</Button>
                            </Box>
                            <Box sx={{display: "flex", justifyContent: "flex-end", mt: 2}}>
                                <Button onClick={handleClose} variant="contained" sx={{backgroundColor: colors.primary}}>
                                    סגור
                                </Button>
                            </Box>
                        </DialogContent>
                    ) : (
                        <form onSubmit={formik.handleSubmit}>
                            <DialogContent sx={{padding: "24px 20px"}}>
                                <Box sx={{display: "flex", flexDirection: "column", gap: 2}}>
                                    <Box>
                                        <Typography sx={labelSx}>*שם</Typography>
                                        <TextField fullWidth size="small" placeholder="Fireberry CRM"
                                                   value={formik.values.name}
                                                   onChange={formik.handleChange("name")}/>
                                        {formik.errors.name && formik.touched.name && (
                                            <Typography sx={{color: colors.error, fontSize: "12px"}}>{formik.errors.name}</Typography>
                                        )}
                                    </Box>
                                    <Box sx={{display: "flex", alignItems: "center", justifyContent: "space-between"}}>
                                        <Typography sx={labelSx}>פעיל</Typography>
                                        <Switch checked={formik.values.enabled}
                                                onChange={e => formik.setFieldValue("enabled", e.target.checked)}/>
                                    </Box>
                                </Box>
                            </DialogContent>
                            <Box sx={{
                                display: "flex", justifyContent: "flex-start", gap: 1.5,
                                padding: "16px 20px", borderTop: borders.default
                            }}>
                                <Button disabled={submitting} type="submit" variant="contained"
                                        sx={{backgroundColor: colors.primary}}>
                                    {isEdit ? "שמור" : "צור"}
                                </Button>
                                <Button onClick={handleClose} variant="outlined">ביטול</Button>
                            </Box>
                        </form>
                    )}
                </Box>
            </RTL>
        </Dialog>
    );
};

export default ApiKeyDialog;
